import { Injectable, signal } from '@angular/core';
import { Capacitor } from '@capacitor/core';
import { SpeechRecognition } from '@capacitor-community/speech-recognition';
import { TextToSpeech } from '@capacitor-community/text-to-speech';
import { IS_NATIVE } from '../shared/platform.util';

// Idioma usado tanto para reconocimiento de voz (STT) como para lectura en voz alta
// (TTS). Fijo en español rioplatense, sin hacerlo configurable por ahora.
const IDIOMA = 'es-AR';

// Servicio de voz para el asistente de IA (LIA): habla → texto y texto → habla, usando
// solo opciones nativas/gratuitas (Web Speech API en la PWA, plugins de Capacitor en
// nativo) — nada de esto pasa por OpenAI ni tiene costo. El texto transcripto se pasa
// tal cual al mismo pipeline de chat que ya existe (enviarMensaje).
@Injectable({
  providedIn: 'root'
})
export class VozAsistenteService {
  // true mientras está grabando/escuchando.
  escuchando = signal(false);

  // Disponibilidad de poder "hablarle" (STT). En nativo se confirma de forma asincrónica
  // contra el plugin (algunos dispositivos no tienen el motor de reconocimiento).
  puedeEscuchar = signal<boolean>(!IS_NATIVE && this.webSpeechDisponible());

  // Disponibilidad de que "te conteste hablando" (TTS). Nativo se asume siempre
  // disponible (iOS/Android traen motor de síntesis de voz de fábrica).
  puedeHablar = signal<boolean>(IS_NATIVE || (typeof window !== 'undefined' && 'speechSynthesis' in window));

  private recognitionWeb: any = null;

  // Voz elegida para leer en voz alta, resuelta una sola vez (perezosamente) contra las
  // voces instaladas en el dispositivo/navegador — sin esto, muchos motores de síntesis
  // ignoran el "lang" del utterance y leen con la voz por defecto del sistema (a veces en
  // inglés, a veces con acento de España en vez de rioplatense).
  private vozNativaIndex: number | undefined;
  private vozNativaResuelta = false;
  private vozWeb: SpeechSynthesisVoice | undefined;
  private vozWebResuelta = false;

  constructor() {
    if (IS_NATIVE) {
      SpeechRecognition.available()
        .then((res) => this.puedeEscuchar.set(!!res.available))
        .catch(() => this.puedeEscuchar.set(false));
    }
  }

  private webSpeechDisponible(): boolean {
    if (typeof window === 'undefined') return false;
    const w = window as any;
    return !!(w.SpeechRecognition || w.webkitSpeechRecognition);
  }

  // true si la escucha en curso fue cancelada explícitamente (tacho), para descartar
  // cualquier transcript parcial que la API llegue a devolver igual al cortar.
  private canceladoActual = false;

  // Inicia la escucha y resuelve con el texto transcripto, o null si el usuario canceló,
  // no se detectó nada, hubo un error, o se denegó el permiso.
  async iniciarEscucha(): Promise<string | null> {
    if (this.escuchando()) return null;
    this.canceladoActual = false;
    this.escuchando.set(true);
    try {
      const resultado = IS_NATIVE ? await this.iniciarEscuchaNativa() : await this.iniciarEscuchaWeb();
      return this.canceladoActual ? null : resultado;
    } finally {
      this.escuchando.set(false);
    }
  }

  // Cancela una escucha en curso (el usuario toca el tacho): descarta cualquier
  // transcript, a diferencia de dejar que termine sola por silencio.
  cancelarEscucha(): void {
    if (!this.escuchando()) return;
    this.canceladoActual = true;
    if (IS_NATIVE) {
      SpeechRecognition.stop().catch(() => {});
    } else {
      this.recognitionWeb?.stop?.();
    }
    this.escuchando.set(false);
  }

  private async iniciarEscuchaNativa(): Promise<string | null> {
    try {
      let permiso = await SpeechRecognition.checkPermissions();
      if (permiso.speechRecognition !== 'granted') {
        permiso = await SpeechRecognition.requestPermissions();
      }
      if (permiso.speechRecognition !== 'granted') {
        console.warn('Permiso de reconocimiento de voz denegado.');
        await this.ofrecerAbrirConfiguracion();
        return null;
      }

      const res = await SpeechRecognition.start({
        language: IDIOMA,
        maxResults: 1,
        popup: false,
        partialResults: false,
      });
      return res.matches?.[0]?.trim() || null;
    } catch (error) {
      console.error('Error en reconocimiento de voz nativo:', error);
      return null;
    }
  }

  private iniciarEscuchaWeb(): Promise<string | null> {
    return new Promise((resolve) => {
      const w = window as any;
      const SpeechRecognitionCtor = w.SpeechRecognition || w.webkitSpeechRecognition;
      if (!SpeechRecognitionCtor) {
        resolve(null);
        return;
      }

      const recognition = new SpeechRecognitionCtor();
      this.recognitionWeb = recognition;
      recognition.lang = IDIOMA;
      recognition.continuous = false;
      recognition.interimResults = false;
      recognition.maxAlternatives = 1;

      recognition.onresult = (event: any) => {
        const texto = event?.results?.[0]?.[0]?.transcript?.trim() || null;
        resolve(texto);
      };
      // onerror/onend: si onresult ya resolvió, estas llamadas a resolve() no hacen nada
      // (una Promise solo resuelve una vez) — cubren el caso de cancelación/sin resultado.
      recognition.onerror = () => resolve(null);
      recognition.onend = () => resolve(null);

      try {
        recognition.start();
      } catch (error) {
        console.error('Error al iniciar reconocimiento de voz web:', error);
        resolve(null);
      }
    });
  }

  private async ofrecerAbrirConfiguracion(): Promise<void> {
    try {
      const { NativeSettings, AndroidSettings, IOSSettings } = await import('capacitor-native-settings');
      const abrir = window.confirm('Para hablarle a LIA por voz, activá el permiso de micrófono desde la configuración del sistema. ¿Querés abrir la configuración ahora?');
      if (!abrir) return;

      if (Capacitor.getPlatform() === 'ios') {
        await NativeSettings.openIOS({ option: IOSSettings.App });
      } else {
        await NativeSettings.openAndroid({ option: AndroidSettings.ApplicationDetails });
      }
    } catch {
      console.warn('No se pudo abrir la configuración del sistema.');
    }
  }

  // Elige, entre una lista de voces disponibles, la que suene más "local": primero
  // exacta es-AR, después cualquier español latinoamericano (es-419/es-US/es-MX/etc.),
  // y si no hay ninguna de esas, cualquier voz en español antes que dejar que el sistema
  // use su default (que en muchos casos ni siquiera es español).
  private elegirMejorVoz<T extends { lang: string }>(voces: T[]): T | undefined {
    const norm = (l: string) => (l || '').toLowerCase().replace('_', '-');
    return (
      voces.find(v => norm(v.lang) === 'es-ar') ||
      voces.find(v => norm(v.lang).startsWith('es') && !norm(v.lang).includes('es-es')) ||
      voces.find(v => norm(v.lang).startsWith('es')) ||
      undefined
    );
  }

  private async prepararVozNativa(): Promise<void> {
    if (this.vozNativaResuelta) return;
    this.vozNativaResuelta = true;
    try {
      const { voices } = await TextToSpeech.getSupportedVoices();
      const elegida = this.elegirMejorVoz(voices);
      this.vozNativaIndex = elegida ? voices.indexOf(elegida) : undefined;
    } catch {
      this.vozNativaIndex = undefined;
    }
  }

  private prepararVozWeb(): Promise<void> {
    if (this.vozWebResuelta) return Promise.resolve();
    return new Promise((resolve) => {
      let resuelto = false;
      const finalizar = () => {
        if (resuelto) return;
        resuelto = true;
        this.vozWeb = this.elegirMejorVoz(window.speechSynthesis.getVoices());
        this.vozWebResuelta = true;
        resolve();
      };
      if (window.speechSynthesis.getVoices().length > 0) {
        finalizar();
      } else {
        // En algunos navegadores la lista de voces se carga de forma asincrónica.
        window.speechSynthesis.addEventListener('voiceschanged', finalizar, { once: true });
        setTimeout(finalizar, 300);
      }
    });
  }

  // Lee un texto en voz alta.
  async hablar(texto: string): Promise<void> {
    const contenido = texto?.trim();
    if (!contenido) return;

    try {
      if (IS_NATIVE) {
        await this.prepararVozNativa();
        await TextToSpeech.speak({
          text: contenido,
          lang: IDIOMA,
          rate: 1.0,
          pitch: 1.0,
          volume: 1.0,
          category: 'ambient',
          ...(this.vozNativaIndex !== undefined ? { voice: this.vozNativaIndex } : {}),
        });
      } else if ('speechSynthesis' in window) {
        await this.prepararVozWeb();
        window.speechSynthesis.cancel();
        const utterance = new SpeechSynthesisUtterance(contenido);
        utterance.lang = IDIOMA;
        if (this.vozWeb) utterance.voice = this.vozWeb;
        window.speechSynthesis.speak(utterance);
      }
    } catch (error) {
      console.error('Error al reproducir voz:', error);
    }
  }

  // Corta cualquier lectura en curso.
  detenerHabla(): void {
    if (IS_NATIVE) {
      TextToSpeech.stop().catch(() => {});
    } else if (typeof window !== 'undefined' && 'speechSynthesis' in window) {
      window.speechSynthesis.cancel();
    }
  }
}
